<template>
    <ValidationErrors :show="showErrors"/>
    <Head :title="'Commission - ' + project.title"/>
    <Audit v-if="openModal" @close="openModal = false" :open="openModal" :activity_logs="currentCommission.activity_logs" />
    <div class="w-full mb-6">
        <ProjectHeader :project="project"></ProjectHeader>
        <div class="space-y-6 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <form @submit.prevent="submit">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="bg-white px-4 p-6 space-y-6">
                            <div class="flex pb-5 border-b border-gray-200 justify-between items-center">
                                <h3 class="text-lg leading-6 font-bold text-gray-900">Commission</h3>
                                <div class="flex text-right items-end gap-3">
                                    <div v-if="!project.client.cmid" class="text-sm text-yellow-700 self-center">
                                        This client is missing a CMID. Please add a CMID to create a commission invoice.
                                    </div>
                                    <Button @click="emitter.emit('openCommissionInvoice', {project: project, quote_services: form.services, organisation_id: form.organisation_id})"
                                            class="shadow-md inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            :disabled="form.services.length === 0 || !project.client.cmid"
                                            :class="{'text-white bg-amaze-coral': form.services.length > 0 && project.client.cmid, 'opacity-50 cursor-not-allowed' : form.services.length === 0 || !project.client.cmid}">
                                        <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                  stroke-width="2"
                                                  d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Create Commission Invoice
                                    </Button>
                                </div>
                            </div>

                            <div class="flex flex-col mt-3 space-y-3">
                                <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                    <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8 space-y-6">
                                        <div class="rounded-md bg-blue-50 p-4">
                                            <div class="flex">
                                                <div class="flex-shrink-0">
                                                    <InformationCircleIcon class="h-5 w-5 text-blue-400"
                                                                           aria-hidden="true"/>
                                                </div>
                                                <div class="ml-3 space-y-3">
                                                    <p class="text-sm text-blue-700">To create a commission invoice for a
                                                        supplier, select your desired service(s) and click 'Create Commission Invoice'. <span
                                                            class="font-bold">You can only generate an invoice for one supplier at a time.</span></p>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                            <table class="min-w-full divide-y divide-gray-200">
                                                <thead class="bg-gray-50">
                                                <tr>
                                                    <th scope="col" class="relative px-6 py-3">
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Service Name
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Service Gross Value
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Commission %
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        VAT %
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        VAT Amount
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Commission (Net)
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Commission (Gross)
                                                    </th>
                                                    <th></th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                <template v-if="Object.keys(organisations).length > 0"
                                                          v-for="(organisation, index) in organisations"
                                                          :key="index">
                                                    <tr
                                                        class="organisation-row bg-gray-200 cursor-pointer"
                                                        :data-organisation-id="organisation.id"
                                                        :class="{'  border-t border-t-gray-400' : index > 0}">
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            <input :id="'organisation-' + organisation.id"
                                                                   @change="invoiceable($event)"
                                                                   :data-organisation-id="organisation.id"
                                                                   :data-services="organisation.commission_ids"
                                                                   type="checkbox"
                                                                   class="organisation-checkbox focus:ring-indigo-500 h-4 w-4 text-pitch-purple border-gray-300 rounded"
                                                                   v-if="!allInvoiced(organisation.commissions)"/>
                                                        </td>
                                                        <td colspan="8" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                            {{ organisation.name }}
                                                        </td>
                                                    </tr>
                                                    <tr class="supplier-row" :data-organisation-id="organisation.id"
                                                        :data-service-id="service.id"
                                                        v-for="(service, idx) in organisation.commissions"
                                                        :class="{'bg-gray-100': idx % 2 }">
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" :class="{' bg-gray-100' : serviceInvoiced(service.id)}">
                                                            <input :id="'service-' + service.id"
                                                                   :data-organisation-id="organisation.id"
                                                                   @change="invoiceableService($event)"
                                                                   :data-service-id="service.id"
                                                                   type="checkbox"
                                                                   class="service-checkbox focus:ring-indigo-500 h-4 w-4 text-pitch-purple border-gray-300 rounded"
                                                                   v-if="!serviceInvoiced(service.id)"
                                                                   />
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" :class="{' bg-gray-100' : serviceInvoiced(service.id)}">
                                                            {{ service.name }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" :class="{' bg-gray-100' : serviceInvoiced(service.id)}">
                                                            {{ formatCurrency(service.gross_buy_total, project.bank_account.currency.code, true) }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" :class="{' bg-gray-100' : serviceInvoiced(service.id)}">
                                                            <span v-if="service.commission_type === 'Percentage'">
                                                                {{ service.commission_amount }}%
                                                            </span>
                                                            <span v-else>
                                                                {{
                                                                    parseFloat((+service.commission_total / (+service.gross_buy_total === 0 ? 100 : service.gross_buy_total)) * 100).toFixed(2)
                                                                }}%
                                                            </span>
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" :class="{' bg-gray-100' : serviceInvoiced(service.id)}">
                                                            {{ service.commission_vat_percentage }}%
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" :class="{' bg-gray-100' : serviceInvoiced(service.id)}">
                                                            {{
                                                                formatCurrency(service.commission_vat, project.bank_account.currency.code, true)
                                                            }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" :class="{' bg-gray-100' : serviceInvoiced(service.id)}">
                                                            {{
                                                                formatCurrency(service.commission_net, project.bank_account.currency.code, true)
                                                            }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" :class="{' bg-gray-100' : serviceInvoiced(service.id)}">
                                                            {{ formatCurrency(service.commission_total, project.bank_account.currency.code, true) }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" :class="{' bg-gray-100' : serviceInvoiced(service.id)}">
                                                            <button type="button" @click="currentCommission = service, openModal = true"
                                                                class="inline-flex ml-3 items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black">
                                                                Show Changelog
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    <tr :data-organisation-id="organisation.id"
                                                        class="totals-row bg-gray-300 cursor-pointer">
                                                        <td colspan="2"
                                                            class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                            Supplier Total
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                            {{
                                                                formatCurrency(organisation.commission_totals.buy_gross_total, project.bank_account.currency.code, true)
                                                            }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                            {{
                                                                formatCurrency(organisation.commission_totals.vat_total, project.bank_account.currency.code, true)
                                                            }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                            {{
                                                                formatCurrency(organisation.commission_totals.commission_total_without_vat, project.bank_account.currency.code, true)
                                                            }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                            {{
                                                                formatCurrency(organisation.commission_totals.commission_total, project.bank_account.currency.code, true)
                                                            }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold"></td>
                                                    </tr>
                                                </template>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </section>
        </div>
    </div>
    <CreateCommissionInvoice/>
    <CreateAdhocCommissionInvoice/>
</template>


<script>
import Authenticated from "@/Layouts/Authenticated.vue";
import {Link, Head, useForm} from "@inertiajs/vue3";
import {
    ClipboardCopyIcon,
    PlusSmIcon,
    UsersIcon,
    InformationCircleIcon,
    ChevronDownIcon,
    CogIcon,
    PencilAltIcon,
    CheckCircleIcon,
    TrashIcon
} from "@heroicons/vue/outline"
import ValidationErrors from "@/Components/ValidationErrors";
import ProjectHeader from "@/Components/ProjectHeader";
import CreateQuote from "@/Components/SlideOuts/CreateQuote";
import {Popover, PopoverButton, PopoverGroup} from "@headlessui/vue";
import Button from "@/Components/Button.vue";

import DuplicateQuote from "@/Components/SlideOuts/DuplicateQuote";
import Pagination from "@/Components/Pagination.vue";
import CreateExpense from "@/Components/SlideOuts/CreateExpense.vue";
import moment from "moment";
import {emitter} from "@/eventHub";
import CreateSupplierInvoice from "@/Components/SlideOuts/CreateSupplierInvoice";
import LinkSupplierInvoiceToInvoice from "@/Components/SlideOuts/LinkSupplierInvoiceToInvoice";
import CreateCommissionInvoice from "@/Components/SlideOuts/CreateCommissionInvoice";
import CreateAdhocCommissionInvoice from "@/Components/SlideOuts/CreateAdhocCommissionInvoice";
import Audit from "@/Components/Modal/Audit.vue";
import { ref } from "vue";

export default {
    layout: Authenticated,
    components: {
        CreateAdhocCommissionInvoice,
        CreateCommissionInvoice,
        DuplicateQuote,
        Button,
        CreateQuote,
        ProjectHeader,
        ValidationErrors,
        Link,
        Head,
        PlusSmIcon,
        UsersIcon,
        InformationCircleIcon,
        Popover,
        PopoverButton,
        ChevronDownIcon,
        PopoverGroup,
        CogIcon,
        PencilAltIcon,
        ClipboardCopyIcon,
        CheckCircleIcon,
        Pagination,
        TrashIcon,
        Audit
    },
    props: {
        project: Object,
        organisations: Object,
        services_invoiced: Object,
    },
    setup(){
        const openModal = ref(false);
        const currentCommission = ref(null);

        return {openModal, currentCommission};
    },
    data() {
        return {
            showErrors: false,
            emitter: emitter,
            form: useForm({
                services: [],
            }),
        }
    },
    created() {
        this.moment = moment
    },
    mounted() {
    },
    methods: {
        invoiceable(event) {

            // If the event target has services, we need to check them.
            let services = event.target.dataset.services.split(',');

            services.forEach(function (service, index) {
                // Target the checkbox for this service and set it to checked.
                let service_checkbox = document.getElementById('service-' + service);

                if (service_checkbox !== null) {
                    if (event.target.checked === true) {
                        service_checkbox.checked = true
                        // Add the services to the services form array.
                        this.form.services.push(service);
                    } else {
                        service_checkbox.checked = false

                        // Remove services from the form array.
                        const index = this.form.services.indexOf(service);
                        if (index !== -1) {
                            this.form.services.splice(index, 1);
                        }
                    }
                }
            }, this);

            this.form.organisation_id = event.target.dataset.organisationId;

            this.disableOtherCheckboxes(event.target.dataset.organisationId, event);

        },
        invoiceableService(event) {

            let service_checkbox = document.getElementById('service-' + event.target.dataset.serviceId);

            if (event.target.checked === true) {
                service_checkbox.checked = true
                // Add the services to the services form array.
                this.form.services.push(event.target.dataset.serviceId);
                this.form.organisation_id = event.target.dataset.organisationId;
            } else {
                service_checkbox.checked = false

                // Remove services from the form array.
                const index = this.form.services.indexOf(event.target.dataset.serviceId);
                if (index !== -1) {
                    this.form.services.splice(index, 1);
                }
            }

            this.disableOtherCheckboxes(event.target.dataset.organisationId, event);


        },
        disableOtherCheckboxes(organisationId, event) {
            // Disable any other checkboxes and organisations from being able to be selected.
            var all_other_checkboxes = document.querySelectorAll('input[data-organisation-id]:not(input[data-organisation-id="' + organisationId + '"])')
            var rows = document.querySelectorAll('tr[data-organisation-id]:not(tr[data-organisation-id="' + organisationId + '"])');

            all_other_checkboxes.forEach(function (service, index) {
                if (event.target.checked === true) {
                    service.setAttribute('disabled', 'disabled');
                    service.classList.add('cursor-not-allowed');
                } else {
                    service.removeAttribute('disabled');
                    service.classList.remove('cursor-not-allowed');
                }
            });

            rows.forEach(function (row, index) {
                if (event.target.checked === true) {
                    row.classList.add('opacity-50');
                    row.classList.add('cursor-not-allowed');
                } else {
                    row.classList.remove('opacity-50');
                    row.classList.remove('cursor-not-allowed');
                }
            });
        },
        // Check if the supplier id is in an invoiced supplier list
        serviceInvoiced(serviceId) {
            return this.services_invoiced.includes(serviceId);
        },
        allInvoiced(services) {
            if (!services || services.length === 0) {
                return false;
            }

            return services.every((service) => this.serviceInvoiced(service.id));
        }
    },
}
</script>
